const SENTENCE_END_RE = /[.!?…][)"'”»]*$/;

/** How hard a word is pushed in the caption (etap B): the "emphasis dial" of the playbook. */
export type CaptionEmphasis = "base" | "strong" | "payoff";

export interface CaptionWord {
  text: string;
  fromMs: number;
  toMs: number;
  emphasis?: CaptionEmphasis;
}

export interface CaptionEvent {
  fromMs: number;
  toMs: number;
  lines: CaptionWord[][];
}

/**
 * Polish orthography forbids ending a line with a one-letter conjunction/preposition
 * ("sierota"); the short two-letter prepositions read just as badly on a big caption.
 */
const POLISH_HANGING_WORDS = new Set([
  "a",
  "i",
  "o",
  "u",
  "w",
  "z",
  "na",
  "do",
  "po",
  "za",
  "ze",
  "we",
  "od",
  "co",
  "to",
  "że",
  "by",
  "ku",
  "aż",
  "bo",
  "no",
]);

const isHangingWord = (text: string) => POLISH_HANGING_WORDS.has(text.toLowerCase().replace(/[^\p{L}]/gu, ""));

/**
 * Groups clip words (already on the OUTPUT timeline) into caption pages of at most
 * `maxLines` lines / `maxCharsPerLine` chars, breaking pages on sentence ends and on
 * pauses. Line breaks never leave a hanging conjunction at the end of a line.
 */
export interface CaptionPagingOptions {
  maxCharsPerLine?: number;
  maxLines?: number;
  pageBreakGapMs?: number;
  /** Words per page cap (etap B "spice": 2–3 words per screen); undefined = chars only. */
  maxWordsPerPage?: number;
  /** Also start a new page after a comma / semicolon / colon (clause break). */
  breakOnClauses?: boolean;
  /** A page shorter than this is merged into its neighbour: flashing captions are unreadable. */
  minPageMs?: number;
}

const CLAUSE_END_RE = /[,;:][)"'”»]*$/;

export const buildCaptionEvents = (
  words: CaptionWord[],
  {
    maxCharsPerLine = 18,
    maxLines = 2,
    pageBreakGapMs = 800,
    maxWordsPerPage,
    breakOnClauses = false,
    minPageMs = 0,
  }: CaptionPagingOptions = {},
): CaptionEvent[] => {
  const events: CaptionEvent[] = [];
  let page: CaptionWord[] = [];

  const maxPageChars = maxCharsPerLine * maxLines;

  const flushPage = (isFinal = false) => {
    if (!page.length) return;

    // A hanging conjunction must not end a caption page either — carry it over as the
    // first word of the next page (unless nothing follows).
    let carried: CaptionWord | null = null;
    if (!isFinal && page.length > 1 && isHangingWord(page[page.length - 1]!.text)) {
      carried = page.pop()!;
    }

    events.push({
      fromMs: page[0]!.fromMs,
      toMs: page[page.length - 1]!.toMs,
      lines: breakIntoLines(page, maxCharsPerLine, maxLines),
    });
    page = carried ? [carried] : [];
  };

  for (const word of words) {
    const previous = page[page.length - 1];
    const pageChars = page.reduce((sum, item) => sum + item.text.length + 1, 0);

    if (
      previous &&
      (word.fromMs - previous.toMs > pageBreakGapMs ||
        pageChars + word.text.length > maxPageChars ||
        (maxWordsPerPage !== undefined && page.length >= maxWordsPerPage) ||
        SENTENCE_END_RE.test(previous.text) ||
        (breakOnClauses && CLAUSE_END_RE.test(previous.text)))
    ) {
      flushPage();
    }

    page.push(word);
  }
  flushPage(true);

  // A page that would flash by is folded into its neighbour, as long as the merged page
  // still fits the word cap (one over is tolerated: better a 4-word page than a flash).
  if (minPageMs > 0) {
    const wordsOf = (entry: CaptionEvent) => entry.lines.flat();
    for (let index = 0; index < events.length; ) {
      const event = events[index]!;
      const spokenMs = event.toMs - event.fromMs;
      const nextIndex = index + 1 < events.length ? index + 1 : index - 1;
      const neighbour = events[nextIndex];
      const mergedChars = neighbour
        ? [...wordsOf(event), ...wordsOf(neighbour)].reduce((sum, word) => sum + word.text.length + 1, -1)
        : Infinity;
      const fits =
        neighbour !== undefined &&
        mergedChars <= maxPageChars + 4 &&
        (maxWordsPerPage === undefined || wordsOf(event).length + wordsOf(neighbour).length <= maxWordsPerPage + 1);
      if (spokenMs < minPageMs && neighbour && fits) {
        const ordered =
          nextIndex > index ? [...wordsOf(event), ...wordsOf(neighbour)] : [...wordsOf(neighbour), ...wordsOf(event)];
        const replacement: CaptionEvent = {
          fromMs: ordered[0]!.fromMs,
          toMs: ordered[ordered.length - 1]!.toMs,
          lines: breakIntoLines(ordered, maxCharsPerLine, maxLines),
        };
        events.splice(Math.min(index, nextIndex), 2, replacement);
        continue;
      }
      index += 1;
    }
  }

  // A page stays on screen until the next one arrives (or its own end + a beat), so the
  // viewer is never staring at an empty lower third mid-sentence.
  for (let index = 0; index < events.length; index += 1) {
    const next = events[index + 1];
    const event = events[index]!;
    event.toMs = next ? Math.min(next.fromMs, event.toMs + 1_500) : event.toMs + 500;
  }

  return events;
};

const breakIntoLines = (words: CaptionWord[], maxCharsPerLine: number, maxLines: number): CaptionWord[][] => {
  const lines: CaptionWord[][] = [];
  let line: CaptionWord[] = [];
  let lineChars = 0;

  for (const word of words) {
    const nextChars = lineChars + (line.length ? 1 : 0) + word.text.length;
    if (line.length && nextChars > maxCharsPerLine && lines.length < maxLines - 1) {
      lines.push(line);
      line = [];
      lineChars = 0;
    }
    line.push(word);
    lineChars += (line.length > 1 ? 1 : 0) + word.text.length;
  }
  if (line.length) lines.push(line);

  // Move a hanging conjunction only when it fits in the next line.
  for (let index = 0; index < lines.length - 1; index += 1) {
    const currentLine = lines[index]!;
    const lastWord = currentLine[currentLine.length - 1];
    const nextLine = lines[index + 1]!;
    const nextLineChars = nextLine.map((word) => word.text).join(" ").length;
    if (
      currentLine.length > 1 &&
      lastWord &&
      isHangingWord(lastWord.text) &&
      nextLineChars + 1 + lastWord.text.length <= maxCharsPerLine
    ) {
      currentLine.pop();
      nextLine.unshift(lastWord);
    }
  }

  return lines.filter((entry) => entry.length > 0).slice(0, maxLines);
};

const NEGATIONS = new Set([
  "nie",
  "nigdy",
  "zawsze",
  "każdy",
  "każda",
  "żaden",
  "żadna",
  "bez",
  "tylko",
  "wszystko",
  "nic",
]);
const EMPHASIS_WORDS = new Set([
  "błąd",
  "błędy",
  "pułapka",
  "prawda",
  "sekret",
  "klucz",
  "dowód",
  "unikaj",
  "przestań",
  "zacznij",
  "koniec",
  "najgorszy",
  "najgorsze",
  "najlepszy",
  "najlepsze",
  "najprostszy",
  "banalnie",
  "kalorii",
  "kalorie",
  "schudnąć",
  "schudniesz",
  "brzuch",
  "metamorfoza",
  "przemiana",
]);
const stripPunctuation = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");

/**
 * Deterministic "caption director" (etap B): which words carry the sentence. Numbers,
 * negations, vivid/list words and long content words get `strong`; the last content word
 * of a sentence is the `payoff`. Capped so a page never shouts every word — the effect lives
 * in the contrast. An LLM director can override per word later; the shape is the same.
 */
export const assignCaptionEmphasis = (words: CaptionWord[]): CaptionWord[] => {
  const result: CaptionWord[] = words.map((word) => ({ ...word, emphasis: "base" as CaptionEmphasis }));
  let sentenceStart = 0;
  const finishSentence = (end: number) => {
    const sentence = result.slice(sentenceStart, end + 1);
    const scored = sentence.map((word, index) => {
      const bare = stripPunctuation(word.text);
      let score = 0;
      if (/\d/.test(bare)) score += 3;
      if (NEGATIONS.has(bare)) score += 2;
      if (EMPHASIS_WORDS.has(bare)) score += 2;
      if (bare.length >= 9) score += 1;
      return { index, score };
    });
    const strongCount = Math.max(1, Math.floor(sentence.length * 0.4));
    for (const { index } of scored
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, strongCount)) {
      sentence[index]!.emphasis = "strong";
    }
    for (let index = sentence.length - 1; index >= 0; index -= 1) {
      if (stripPunctuation(sentence[index]!.text).length >= 4) {
        sentence[index]!.emphasis = "payoff";
        break;
      }
    }
    sentenceStart = end + 1;
  };
  for (let index = 0; index < result.length; index += 1) {
    if (SENTENCE_END_RE.test(result[index]!.text) || index === result.length - 1) finishSentence(index);
  }
  return result;
};

export interface CaptionStyle {
  fontName: string;
  fontSize: number;
  /** &HAABBGGRR — highlight colour a word turns into while spoken. */
  primaryColour: string;
  /** &HAABBGGRR — base colour of not-yet-spoken words. */
  secondaryColour: string;
  outlineColour: string;
  backColour: string;
  outline: number;
  shadow: number;
  marginV: number;
  bold: boolean;
}

/** `#RRGGBB` → ASS `&H00BBGGRR` (ASS keeps channels reversed and alpha first). */
export const hexToAssColor = (hex: string): string => {
  const normalized = hex.replace("#", "").padStart(6, "0").slice(0, 6);
  const r = normalized.slice(0, 2);
  const g = normalized.slice(2, 4);
  const b = normalized.slice(4, 6);
  return `&H00${b}${g}${r}`.toUpperCase();
};

const formatAssTime = (ms: number) => {
  const clamped = Math.max(0, Math.round(ms / 10) * 10);
  const cs = Math.floor((clamped % 1000) / 10);
  const totalSeconds = Math.floor(clamped / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
};

const escapeAssText = (text: string) => text.replace(/\\/g, "\\\\").replace(/{/g, "(").replace(/}/g, ")");

/**
 * ASS with karaoke tags: `\k` flips each word from SecondaryColour to PrimaryColour at its
 * spoken moment — the per-word highlight the playbook renders with Remotion comes out of
 * libass for free, with zero new dependencies (decision recorded on TRE-1979).
 */
export const buildAssSubtitles = (events: CaptionEvent[], style: CaptionStyle, playResX = 1080, playResY = 1920) => {
  const header = [
    "[Script Info]",
    "ScriptType: v4.00+",
    `PlayResX: ${playResX}`,
    `PlayResY: ${playResY}`,
    "ScaledBorderAndShadow: yes",
    "WrapStyle: 2",
    "",
    "[V4+ Styles]",
    "Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding",
    `Style: Brand,${style.fontName},${style.fontSize},${style.primaryColour},${style.secondaryColour},${style.outlineColour},${style.backColour},${style.bold ? -1 : 0},0,0,0,100,100,0,0,1,${style.outline},${style.shadow},2,60,60,${style.marginV},1`,
    "",
    "[Events]",
    "Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text",
  ];

  const lines = events.map((event) => {
    const parts: string[] = [];
    let cursorMs = event.fromMs;

    event.lines.forEach((lineWords, lineIndex) => {
      if (lineIndex > 0) parts.push("\\N");
      lineWords.forEach((word, wordIndex) => {
        // The karaoke counter starts at the event start: leading/inter-word gaps are folded
        // into each word's \k so highlights stay in sync with the audio.
        const durationCs = Math.max(1, Math.round((word.toMs - cursorMs) / 10));
        cursorMs = word.toMs;
        // The libass fallback has no weight axis: strong / payoff words simply go bold.
        const bold = word.emphasis === "strong" || word.emphasis === "payoff";
        parts.push(
          `{\\k${durationCs}${bold ? "\\b1" : ""}}${wordIndex > 0 ? " " : ""}${escapeAssText(word.text)}${bold ? "{\\b0}" : ""}`,
        );
      });
    });

    return `Dialogue: 0,${formatAssTime(event.fromMs)},${formatAssTime(event.toMs)},Brand,,0,0,0,,${parts.join("")}`;
  });

  return [...header, ...lines, ""].join("\n");
};
